import { useState, useEffect } from 'react';
import kit from './kit.module.css';
import styles from './VoiceSilenceDemo.module.css';

type Mode = 'good' | 'bad';

interface TimelineEvent {
  time: string;
  type: 'user' | 'agent' | 'silence' | 'warning';
  text: string;
  delay: number; // ms from start
}

const EVENTS: Record<Mode, TimelineEvent[]> = {
  good: [
    { time: '0.0s', type: 'user', text: '"Update my CV with my new job."', delay: 0 },
    { time: '0.3s', type: 'agent', text: '"Got it, one moment..."', delay: 900 },
    { time: '0.8s', type: 'agent', text: '"Processing your request..."', delay: 1700 },
    { time: '2.1s', type: 'agent', text: '"Almost there..."', delay: 2600 },
    { time: '3.0s', type: 'agent', text: '"Your CV is ready to review."', delay: 3500 },
  ],
  bad: [
    { time: '0.0s', type: 'user', text: '"Update my CV with my new job."', delay: 0 },
    { time: '...', type: 'silence', text: '4 seconds of silence', delay: 900 },
    { time: '4.0s', type: 'user', text: '"Hello? Did that work?"', delay: 3600 },
    { time: '4.3s', type: 'warning', text: 'Processes both inputs at once', delay: 4400 },
    { time: '4.5s', type: 'warning', text: '"I received two requests at once. I\'m not sure which to follow."', delay: 5200 },
  ],
};

const VERDICT: Record<Mode, string> = {
  good: 'Short spoken updates filled every pause, so the user never wondered if they were heard.',
  bad: '4 seconds of silence made the user repeat themselves, and the agent got confused.',
};

function EventLine({ event }: { event: TimelineEvent }) {
  if (event.type === 'silence') {
    return (
      <div className={`${kit.cue} ${styles.silence}`}>
        <span className={styles.time}>{event.time}</span> ({event.text})
      </div>
    );
  }

  const isUser = event.type === 'user';
  const isWarning = event.type === 'warning';

  return (
    <div
      className={`${kit.voiceLine} ${styles.line} ${isUser ? '' : kit.voiceAgent} ${isWarning ? styles.warning : ''}`}
    >
      <span className={styles.time}>{event.time}</span>
      <span className={kit.voiceWho}>{isUser ? 'You' : 'Agent'}</span>
      <p>{event.text}</p>
    </div>
  );
}

export function VoiceSilenceDemo() {
  const [mode, setMode] = useState<Mode | null>(null);
  const [run, setRun] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);

  const events = mode ? EVENTS[mode] : [];
  const allVisible = mode !== null && visibleCount >= events.length;
  const playing = mode !== null && !allVisible;

  useEffect(() => {
    setVisibleCount(0);
    if (!mode) return;
    const timers = EVENTS[mode].map((event, i) => setTimeout(() => setVisibleCount(i + 1), event.delay));
    return () => timers.forEach(clearTimeout);
  }, [mode, run]);

  function start(next: Mode) {
    setMode(next);
    setRun((r) => r + 1);
  }

  const controls = (
    <div className={`${kit.row} ${styles.controls}`}>
      <button className={`${kit.btn} ${kit.btnPrimary}`} onClick={() => start('good')}>
        Simulate good interaction
      </button>
      <button className={`${kit.btn} ${kit.btnEdit}`} onClick={() => start('bad')}>
        Simulate bad interaction
      </button>
    </div>
  );

  return (
    <div className={kit.root}>
      <div className={`${kit.orb} ${playing ? kit.orbActive : ''}`} aria-hidden="true" />

      {!mode && (
        <>
          <p className={`${kit.feedback} ${styles.center}`}>Voice assistant ready. Hear how it handles a slow request.</p>
          {controls}
        </>
      )}

      {mode && (
        <>
          <div className={`${kit.row} ${styles.controls}`}>
            <span className={`${kit.chip} ${mode === 'good' ? kit.chipGood : kit.chipBad}`}>
              {mode === 'good' ? 'Good interaction' : 'Bad interaction'}
            </span>
          </div>

          <div className={kit.transcript} aria-live="polite">
            {events.slice(0, visibleCount).map((event, i) => (
              <EventLine key={`${run}-${i}`} event={event} />
            ))}
          </div>

          {allVisible && (
            <>
              <hr className={kit.divider} />
              <p className={kit.feedback}>{VERDICT[mode]}</p>
              {controls}
            </>
          )}
        </>
      )}
    </div>
  );
}
